import React, { useEffect, useState, useCallback } from "react";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";
import "../../stiluri/GalerieFoto.css";

const GalerieFoto = ({ id }) => {
  const storage = getStorage();
  const rootPath = `${id}/galerie_foto/`;

  const [tree, setTree] = useState(null);
  const [expandedPaths, setExpandedPaths] = useState(new Set([rootPath]));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(rootPath);
  

  // Încarcă doar structura folderelor, fără URL-uri
  const loadFolderStructure = useCallback(async (path) => {
    try {
      const folderRef = ref(storage, path);
      const res = await listAll(folderRef);

      const folders = await Promise.all(
        res.prefixes.map((folderRef) =>
          loadFolderStructure(folderRef.fullPath)
        )
      );

      const files = res.items.map((itemRef) => ({
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: null, // lazy
      }));

      return {
        name: path.split("/").filter(Boolean).pop() || "root",
        path,
        folders,
        files,
      };
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [storage]);

  // Lazy load pentru fișierele din folder
  const loadFilesForNode = async (node) => {
    const folderRef = ref(storage, node.path);
    const res = await listAll(folderRef);
    const files = await Promise.all(
      res.items.map(async (itemRef) => ({
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: null, // lazy
      }))
    );
    return files;
  };

  const expandNode = async (path, currentNode) => {
    setExpandedPaths((prev) => new Set(prev).add(path));

    if (currentNode && currentNode.files.length === 0) {
      const files = await loadFilesForNode(currentNode);
      const updateTree = (node) => {
        if (node.path === path) {
          return { ...node, files };
        }
        return {
          ...node,
          folders: node.folders.map(updateTree),
        };
      };
      setTree((prevTree) => updateTree(prevTree));
    }
  };

  const handlePreview = async (file) => {
    if (!file.url) {
      const url = await getDownloadURL(ref(storage, file.fullPath));
      setPreview({ ...file, url });
    } else {
      setPreview(file);
    }
  };

  useEffect(() => {
    if (!id) {
      setError("ID-ul beneficiarului nu este definit.");
      return;
    }

    setLoading(true);
    loadFolderStructure(rootPath)
      .then((data) => {
        setTree(data);
        setSelectedFolder(rootPath);
        setLoading(false);
        setError(null);
      })
      .catch(() => {
        setError("Eroare la încărcarea galeriei.");
        setLoading(false);
      });
  }, [id, loadFolderStructure, rootPath]);

  const toggleExpand = async (path, node) => {
    if (expandedPaths.has(path)) {
      setExpandedPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(path);
        return newSet;
      });
    } else {
      await expandNode(path, node);
    }
  };

  const handleDeleteFile = async (file) => {
    if (window.confirm(`Ștergi fișierul "${file.name}"?`)) {
      try {
        const fileRef = ref(storage, file.fullPath);
        await deleteObject(fileRef);
        alert("Fișier șters cu succes!");
await expandNode(file.fullPath.substring(0, file.fullPath.lastIndexOf('/')), tree);

        setPreview(null);
      } catch (err) {
        alert("Eroare la ștergerea fișierului.");
        console.error(err);
      }
    }
  };

  const getAllFolders = (node) => {
    if (!node) return [];
    let foldersList = [{ name: node.name, path: node.path }];
    node.folders.forEach((folder) => {
      foldersList = foldersList.concat(getAllFolders(folder));
    });
    return foldersList;
  };

 const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Alege cel puțin un fișier!");
      return;
    }
    setUploading(true);
    try {
      const pathWithSlash = selectedFolder.endsWith('/')
        ? selectedFolder
        : selectedFolder + '/';

      // Încarcă fișierele una câte una în paralel (sau poți face în serie)
      await Promise.all(
        selectedFiles.map((file) => {
          const fileRef = ref(storage, pathWithSlash + file.name);
          return uploadBytes(fileRef, file);
        })
      );

      alert("Încărcare reușită!");
      await expandNode(selectedFolder, tree);

      setSelectedFiles([]);
      setShowUploadModal(false);
    } catch (err) {
      alert("Eroare la upload.");
      console.error(err);
    }
    setUploading(false);
  };

  const handleDragOver = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const handleDrop = (e) => {
  e.preventDefault();
  e.stopPropagation();
  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) {
    setSelectedFiles((prev) => [...prev, ...files]);
  }
};


  const renderNode = (node) => {
    if (!node) return null;

    const isExpanded = expandedPaths.has(node.path);

    return (
      <div className="folder" key={node.path}>
        <div
          className="folder-name"
          onClick={() => toggleExpand(node.path, node)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") toggleExpand(node.path, node);
          }}
        >
          {isExpanded ? "📂" : "📁"} {node.name}
        </div>

        {isExpanded && (
          <div className="folder-contents">
            {node.folders.map((folder) => renderNode(folder))}
            {node.files.map((file) => (
              <div className="file" key={file.fullPath}>
                <span
                  className="file-name"
                  onClick={() => handlePreview(file)}
                  role="button"
                  tabIndex={0}
                >
                  📄 {file.name}
                </span>
                <button
                  className="preview-btn"
                  onClick={() => handlePreview(file)}
                  title="Previzualizează"
                >
                  👁️
                </button>
                <a
                  href={file.url || "#"}
                  onClick={(e) => {
                    if (!file.url) e.preventDefault();
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  ⬇️
                </a>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteFile(file)}
                  title="Șterge"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="galerie-foto-container">
      <h2>Galerie Foto</h2>
      {loading && <p>Se încarcă galeria foto...</p>}
      {error && <p className="error-msg">{error}</p>}
      {tree ? renderNode(tree) : <p>Te rugăm să aștepți.</p>}

      {preview && (
        <div className="preview-overlay" onClick={() => setPreview(null)}>
          <div
            className="preview-content"
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <button className="close-btn" onClick={() => setPreview(null)}>
              ×
            </button>
            <h3>{preview.name}</h3>
            {preview.url ? (
              <>
                {preview.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                  <img
                    src={preview.url}
                    alt={preview.name}
                    className="preview-image"
                  />
                ) : preview.name.match(/\.pdf$/i) ? (
                  <iframe
                    src={preview.url}
                    title={preview.name}
                    className="preview-pdf"
                  />
                ) : (
                  <p>Preview indisponibil.</p>
                )}
              </>
            ) : (
              <p>Se încarcă...</p>
            )}
          </div>
        </div>
      )}

      <button
        className="upload-btn"
        onClick={() => setShowUploadModal(true)}
        style={{ marginTop: "1rem" }}
      >
        Încarcă fișier
      </button>

{showUploadModal && (
  <div
    className="modal-overlay"
    onClick={() => setShowUploadModal(false)}
    onDragOver={handleDragOver}  // <== aici
    onDrop={handleDrop}          // <== și aici
  >
    <div
      className="modal-content"
      onClick={(e) => e.stopPropagation()}
      tabIndex={-1}
      onDragOver={handleDragOver}  // <== și aici, ca să prindă bine drop-ul și în interior
      onDrop={handleDrop}          // <== și aici
      style={{
        border: "2px dashed #888",
        padding: "20px",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
            <button
              className="close-btn"
              onClick={() => setShowUploadModal(false)}
            >
              ×
            </button>
            <h3>Încarcă fișier</h3>
                        <p>Trage fișierele aici sau folosește selectorul de fișiere.</p>

            <form onSubmit={handleUploadSubmit}>
              <label htmlFor="folderSelect">Folder:</label>
              <select
                id="folderSelect"
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
              >
                {tree &&
                  getAllFolders(tree).map((folder) => (
                    <option key={folder.path} value={folder.path}>
                      {folder.path}
                    </option>
                  ))}
              </select>

              <input
                type="file"
                multiple
onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
               required={selectedFiles.length === 0}
  disabled={uploading}
  style={{ marginTop: "1rem" }}
/>
              {selectedFiles.length > 0 && (
                <p>{selectedFiles.length} fișier(e) selectat(e)</p>
              )}
              <button type="submit" disabled={uploading}>
                {uploading ? "Se încarcă..." : "Încarcă"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalerieFoto;
